import { Dispatch } from 'react'
import ReactGA from 'react-ga'
import { AppState } from '../../../../global/store'
import { ApiResponse } from '../../../types'
import * as api from '../services/api'
import { Country, Timezone, emptyAddress, Address } from './store'

import {
  LOAD_TIMEZONES_REQUEST,
  LOAD_TIMEZONES_RECEIVED,
  LOAD_TIMEZONES_ERROR,
  LOAD_COUNTRIES_REQUEST,
  LOAD_COUNTRIES_RECEIVED,
  LOAD_COUNTRIES_ERROR,
  UPDATE_ADDRESS_SETTINGS_FIELD,
  LOAD_ADDRESS_REQUEST,
  LOAD_ADDRESS_RECEIVED,
  LOAD_ADDRESS_ERROR,
  UPDATE_ADDRESS_REQUEST,
  UPDATE_ADDRESS_RECEIVED,
  UPDATE_ADDRESS_ERROR,
  UPDATE_ADDRESS_RESET
} from './reducers'

export type AddressSettingsAction =
  | { type: typeof LOAD_TIMEZONES_REQUEST }
  | { type: typeof LOAD_TIMEZONES_RECEIVED, timezones: Timezone[] }
  | { type: typeof LOAD_TIMEZONES_ERROR }
  | { type: typeof LOAD_COUNTRIES_REQUEST }
  | { type: typeof LOAD_COUNTRIES_RECEIVED, countries: Country[] }
  | { type: typeof LOAD_COUNTRIES_ERROR }
  | { type: typeof UPDATE_ADDRESS_SETTINGS_FIELD, field: string, value?: string | number }
  | { type: typeof LOAD_ADDRESS_REQUEST }
  | { type: typeof LOAD_ADDRESS_RECEIVED, address: Address, noAddress: boolean }
  | { type: typeof LOAD_ADDRESS_ERROR }
  | { type: typeof UPDATE_ADDRESS_REQUEST }
  | { type: typeof UPDATE_ADDRESS_RECEIVED }
  | { type: typeof UPDATE_ADDRESS_ERROR }
  | { type: typeof UPDATE_ADDRESS_RESET }

export function updateField(field: string, value?: string | number) {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    dispatch({
      type: UPDATE_ADDRESS_SETTINGS_FIELD,
      field,
      value
    })
  }
}

export function loadTimezones() {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    try {
      dispatch({
        type: LOAD_TIMEZONES_REQUEST
      })

      const response = await api.getTimezones()
      const mappedTimezones = response.data.map((data: ApiResponse) => mapTimezone(data))

      dispatch({
        type: LOAD_TIMEZONES_RECEIVED,
        timezones: mappedTimezones
      })
    } catch {
      dispatch({
        type: LOAD_TIMEZONES_ERROR
      })
    }
  }
}

export function loadCountries() {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    try {
      dispatch({
        type: LOAD_COUNTRIES_REQUEST
      })

      const response = await api.getCountries()
      const mappedCountries = response.data.map((data: ApiResponse) => mapCountry(data))

      dispatch({
        type: LOAD_COUNTRIES_RECEIVED,
        countries: mappedCountries
      })
    } catch {
      dispatch({
        type: LOAD_COUNTRIES_ERROR
      })
    }
  }
}

export function loadAddress(accountId: string) {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    try {
      dispatch({
        type: LOAD_ADDRESS_REQUEST
      })

      const response = await api.getAddresses(accountId)
      const mappedAddresses = response.data.addresses.data.map((data: ApiResponse) => mapAddress(data))

      if (mappedAddresses.length > 0) {
        dispatch({
          type: LOAD_ADDRESS_RECEIVED,
          address: mappedAddresses[0],
          noAddress: false
        })
      } else {
        dispatch({
          type: LOAD_ADDRESS_RECEIVED,
          address: emptyAddress,
          noAddress: true
        })
      }
    } catch (error) {
      dispatch({
        type: LOAD_ADDRESS_ERROR
      })
    }
  }
}

export function updateAddress(accountId: string, address: Address) {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    try {
      const noAddress = state.settings.address.noAddress

      dispatch({
        type: UPDATE_ADDRESS_REQUEST
      })

      if (noAddress) {
        await api.postAddress(accountId, address)
      } else {
        await api.patchAddress(accountId, address)
      }

      ReactGA.event({
        category: 'account-settings',
        action: 'Updated Address'
      })

      dispatch({
        type: UPDATE_ADDRESS_RECEIVED
      })
    } catch {
      dispatch({
        type: UPDATE_ADDRESS_ERROR
      })
    }
  }
}

export function updateAddressReset() {
  return async function (state: AppState, dispatch: Dispatch<AddressSettingsAction>) {
    dispatch({
      type: UPDATE_ADDRESS_RESET
    })
  }
}

function mapCountry(data: ApiResponse) {
  return {
    id: data.id,
    name: data.name
  }
}

function mapTimezone(data: ApiResponse) {
  return {
    id: data.id,
    name: data.name,
    offset: data.offset_formatted
  }
}

function mapAddress(data: ApiResponse) {
  return {
    id: data.id,
    businessName: data.address_line_one,
    addressLine1: data.address_line_two,
    addressLine2: data.address_line_three,
    postcode: data.postcode,
    city: data.locality,
    region: data.region,
    country: data.country
  }
}
